import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Row, Col, Spinner, ProgressBar } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import BASE_URL from "../config";


const UpdateApplicationStatus = ({ applicationId, applicationData, onStatusUpdate }) => {
  const [showDefaults, setShowDefaults] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState("0");
  const [loading, setLoading] = useState(false);

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(""); // "success" or "error"
  const [popupMessage, setPopupMessage] = useState("");
  const [countdown, setCountdown] = useState(3); // Countdown in seconds

  useEffect(() => {
    if (showPopup) {
      setCountdown(2); // Reset countdown
      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      const timer = setTimeout(() => {
        clearInterval(interval);

        // Wait 1 additional second after countdown finishes
        setTimeout(() => {
          setShowPopup(false);
        }, 1000);
      }, 3000);

      return () => {
        clearInterval(interval);
        clearTimeout(timer);
      };
    }
  }, [showPopup]);

  const handleShow = () => {
    setShowDefaults(true); // Show modal
  };

  const handleCloses = () => {
    setShowDefaults(false); // Close modal
  };

  const handleStatusChange = (e) => {
    setApplicationStatus(e.target.value); // Update application status
  };

  const handleSave = async () => {
    if (!applicationId) {
      setPopupType("error");
      setPopupMessage("No application selected.");
      setShowPopup(true);
      return;
    }
    setLoading(true);

    try {
      // Update application status
      const response = await axios.put(`${BASE_URL}/api.php`, {
        type: "updateApplicationStatus",
        id: applicationId,
        application_status: applicationStatus,
        updated_by_userId: 1, // Replace with logged-in user ID
      });

      if (response.data.message) {
        const email = applicationData?.user_email;
        if (!email) {
          setPopupType("error");
          setPopupMessage("Email not found for this application.");
          setShowPopup(true);
          return;
        }

        const statusMessages = {
          "0": "Application Approved",
          "1": "Application Declined",
          "2": "Application Suspended",
          "3": "Incomplete Application",
          "4": "Waiting For Merchant",
          "5": "Waiting For Bank",
          "6": "Waiting For Agent",
          "7": "Application Applied",
        };

        const statusMessage = statusMessages[applicationStatus] || "Unknown Status";

        const notificationPayload = {
          type: "AddNotification",
          email: email,
          title: `Application Status Updated for Application id ${applicationId}`,
          message: `The application status has been updated to: ${statusMessage}.`,
        };

        await axios.post(`${BASE_URL}/api.php`, notificationPayload);

        setPopupType("success");
        setPopupMessage("Application status updated and notification sent successfully!");
        setShowPopup(true);

        setShowDefaults(false);

        if (onStatusUpdate) {
          onStatusUpdate();
        }
      } else {
        setPopupType("error");
        setPopupMessage("Failed to update application status.");
        setShowPopup(true);
      }
    } catch (error) {
      setPopupType("error");
      setPopupMessage("Error updating application status or sending notification.");
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column justify-content-between">
      {/* Update Application Status Button */}
      <Button variant="primary" className="mt-3 mx-1 mb-4" onClick={handleShow}>
        Update Application Status
      </Button>

      {/* Modal for updating status */}
      <Modal as={Modal.Dialog} centered show={showDefaults} onHide={handleCloses}>
        <Form>
          <Modal.Header>
            <Modal.Title className="h6">Update Application Status</Modal.Title>
            <Button variant="close" aria-label="Close" onClick={handleCloses} />
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group id="application">
                  <Form.Label>Application Status</Form.Label>
                  <Form.Select
                    defaultValue="0"
                    value={applicationStatus}
                    onChange={handleStatusChange}
                  >
                    <option value="0">Application Approved</option>
                    <option value="1">Application Declined</option>
                    <option value="2">Application Suspended</option>
                    <option value="3">Incomplete Application</option>
                    <option value="4">Waiting For Merchant</option>
                    <option value="5">Waiting For Bank</option>
                    <option value="6">Waiting For Agent</option>
                    <option value="7">Application Applied</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleSave} disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button variant="danger" className="ms-auto" onClick={handleCloses}>
              Close
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Popup */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(255, 255, 255, 0.9)",
            borderRadius: "10px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
            padding: "20px 30px",
            zIndex: 1050,
            textAlign: "center",
          }}
        >
          <FontAwesomeIcon
            icon={popupType === "success" ? faCheckCircle : faExclamationCircle}
            className={popupType === "success" ? "text-success" : "text-danger"}
            size="3x"
          />
          <h5 className="mt-3 fw-bold">{popupMessage}</h5>
          <div className="mt-3">
            <ProgressBar
              now={(countdown / 3) * 100}
              striped
              variant={popupType === "success" ? "success" : "danger"}
              style={{ transition: "width 1s linear" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateApplicationStatus;
